'use client';

import { useMemo, useState } from 'react';

type WageType = 'low' | 'avg' | 'spec';
type MonitoringType = '12' | '24';

const wages: Record<WageType, number> = { low: 15, avg: 19, spec: 50 };
const monitoringLabels: Record<MonitoringType, string> = {
  '12': 'After Hours (12 hours/day)',
  '24': '24/7 Monitoring (24 hours/day)',
};

function formatCurrency(value: number) {
  return `$${Math.round(value).toLocaleString()}`;
}

export function RemoteGuardingCalculator() {
  const [camCount, setCamCount] = useState('');
  const [guardHours, setGuardHours] = useState('');
  const [monitoringType, setMonitoringType] = useState<MonitoringType>('12');
  const [wageType, setWageType] = useState<WageType>('avg');
  const [calculationRequested, setCalculationRequested] = useState(false);
  const [validationError, setValidationError] = useState(false);

  const parsedCams = Number(camCount);
  const parsedHours = Number(guardHours);
  const hasCams = Number.isFinite(parsedCams) && parsedCams > 0;

  const results = useMemo(() => {
    if (!hasCams || !calculationRequested) {
      return null;
    }
    const monthlyRate = monitoringType === '12' ? 85 : 135;
    const annualMonitoring = monthlyRate * parsedCams * 12;
    const setupCost = parsedCams * 35;
    const yearlyRemote = annualMonitoring + setupCost;

    const hourlyWage = wages[wageType];
    const yearlyGuard = hourlyWage * (Number.isFinite(parsedHours) ? parsedHours : 0) * 365;

    return {
      monthlyRate,
      annualMonitoring,
      setupCost,
      yearlyRemote,
      yearlyGuard,
      savings: yearlyGuard - yearlyRemote,
    };
  }, [calculationRequested, hasCams, monitoringType, parsedCams, parsedHours, wageType]);

  function calculateCosts() {
    if (!hasCams) {
      setCalculationRequested(false);
      setValidationError(true);
      return;
    }

    setValidationError(false);
    setCalculationRequested(true);
  }

  return (
    <div className="rounded-md bg-white p-8 shadow-lg">
      <h2 className="mb-6 text-2xl font-semibold text-[var(--scw-navy)]">Cost Calculator</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <label className="block text-sm font-semibold text-[var(--scw-navy)]">
            Number of Cameras to Monitor
          </label>
          <input
            id="camCount"
            type="number"
            min={0}
            placeholder="e.g. 6"
            value={camCount}
            onChange={(event) => setCamCount(event.target.value)}
            className="mt-2 h-12 w-full rounded-md border-2 border-[#f2f5f7] bg-[#f2f5f7] px-3 text-base text-slate-700"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--scw-navy)]">
            Hours Per Day of Guard Coverage
          </label>
          <input
            id="guardHours"
            type="number"
            min={0}
            placeholder="e.g. 8"
            value={guardHours}
            onChange={(event) => setGuardHours(event.target.value)}
            className="mt-2 h-12 w-full rounded-md border-2 border-[#f2f5f7] bg-[#f2f5f7] px-3 text-base text-slate-700"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--scw-navy)]">
            Remote Guarding Monitoring Type
          </label>
          <select
            id="monitoringType"
            value={monitoringType}
            onChange={(event) => setMonitoringType(event.target.value as MonitoringType)}
            className="mt-2 h-12 w-full rounded-md border-2 border-[#f2f5f7] bg-[#f2f5f7] px-3 text-base text-slate-700"
          >
            <option value="12">After Hours (12 hours/day)</option>
            <option value="24">24/7 Monitoring (24 hours/day)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--scw-navy)]">
            Guard Wage Level
          </label>
          <select
            id="wageType"
            value={wageType}
            onChange={(event) => setWageType(event.target.value as WageType)}
            className="mt-2 h-12 w-full rounded-md border-2 border-[#f2f5f7] bg-[#f2f5f7] px-3 text-base text-slate-700"
          >
            <option value="low">Low Wage Guard</option>
            <option value="avg">Average Wage Guard</option>
            <option value="spec">Specialist / High Wage Guard</option>
          </select>
        </div>
      </div>

      {validationError && (
        <p className="mt-4 text-sm text-red-500">Please enter a valid number of cameras.</p>
      )}

      <button
        type="button"
        className="mt-6 rounded-md bg-[var(--scw-orange)] px-6 py-3 text-base font-semibold text-white"
        onClick={calculateCosts}
      >
        Calculate Savings
      </button>

      <hr className="my-10 border-slate-200" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
        <div>
          <h4 className="text-base font-semibold text-[var(--scw-navy)]">
            Remote Guarding
            <br />
            <span className="text-xs font-normal text-[var(--scw-gray-muted)]">
              (Annual Cost)
            </span>
          </h4>
          <h2 id="remoteCost" className="mt-2 text-3xl md:text-4xl font-bold text-[#1b5078]">
            {results ? formatCurrency(results.yearlyRemote) : '$0'}
          </h2>
        </div>
        <div>
          <h4 className="text-base font-semibold text-[var(--scw-navy)]">
            On-Site Guard
            <br />
            <span className="text-xs font-normal text-[var(--scw-gray-muted)]">
              (Annual Cost)
            </span>
          </h4>
          <h2 id="guardCost" className="mt-2 text-3xl md:text-4xl font-bold text-[#b30000]">
            {results ? formatCurrency(results.yearlyGuard) : '$0'}
          </h2>
        </div>
        <div>
          <h4 className="text-base font-semibold text-[var(--scw-navy)]">You Save</h4>
          <h2 id="savings" className="mt-2 text-3xl md:text-4xl font-bold text-[#eb8322]">
            {results ? formatCurrency(results.savings) : '$0'}
          </h2>
        </div>
      </div>

      <div
        id="breakdown"
        className="mt-8 rounded-md border-l-4 border-[var(--scw-orange)] bg-[#F7F9FB] p-6"
        hidden={!results}
      >
        {results ? (
          <>
          <h4 className="mb-3 text-base font-semibold text-[var(--scw-navy)]">
            Remote Guarding Cost Breakdown
          </h4>
          <p className="text-sm text-[var(--scw-gray-body)]">
            <strong>Monitoring Type:</strong> {monitoringLabels[monitoringType]}
          </p>
          <p className="mt-2 text-sm text-[var(--scw-gray-body)]">
            <strong>Monitoring Cost:</strong> ${results.monthlyRate}/camera/month × {parsedCams}{' '}
            cameras × 12 months ={' '}
            <strong>{formatCurrency(results.annualMonitoring)}</strong>
          </p>
          <p className="mt-2 text-sm text-[var(--scw-gray-body)]">
            <strong>Setup Fee:</strong> $35 × {parsedCams} cameras ={' '}
            <strong>{formatCurrency(results.setupCost)}</strong>
          </p>
          <p className="mt-3 text-base">
            <strong>Total Remote Guarding Cost:</strong>{' '}
            <span className="font-bold text-[#1B5078]">
              {formatCurrency(results.yearlyRemote)}
            </span>
          </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
